import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Mail, RefreshCw, AlertCircle, CheckCircle2, ShieldOff, Clock, Eye } from "lucide-react";
import { toast } from "sonner";

type EmailMetadata = {
  subject?: string | null;
  html?: string | null;
  text?: string | null;
} | null;

type Row = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
  metadata: EmailMetadata;
};

type Range = "24h" | "7d" | "30d" | "all";

const RANGE_LABEL: Record<Range, string> = {
  "24h": "Ostatnie 24h",
  "7d": "Ostatnie 7 dni",
  "30d": "Ostatnie 30 dni",
  all: "Wszystko",
};

const STATUS_LABEL: Record<string, string> = {
  sent: "Wysłany",
  pending: "W kolejce",
  dlq: "Nieudane",
  failed: "Błąd",
  suppressed: "Zablokowany",
  bounced: "Odbity",
  complained: "Spam",
};

const statusVariant = (s: string) => {
  if (s === "sent") return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200";
  if (s === "pending") return "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200";
  if (s === "suppressed" || s === "bounced" || s === "complained")
    return "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200";
  return "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"; // dlq, failed
};

const PAGE_SIZE = 50;

export const EmailLogTab = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<Range>("7d");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [previewRow, setPreviewRow] = useState<Row | null>(null);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("email_send_log")
      .select("id,message_id,template_name,recipient_email,status,error_message,created_at,metadata")
      .order("created_at", { ascending: false })
      .limit(2000);

    if (range !== "all") {
      const days = range === "24h" ? 1 : range === "7d" ? 7 : 30;
      const since = new Date(Date.now() - days * 86400_000).toISOString();
      q = q.gte("created_at", since);
    }

    const { data, error } = await q;
    if (error) {
      toast.error("Nie udało się pobrać logu wysyłek");
      setRows([]);
    } else {
      setRows((data ?? []) as Row[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  // Deduplikacja po message_id - bierzemy najnowszy wpis, ale uzupełniamy metadata
  // ze starszych wpisów (np. 'pending') jeśli nowszy jej nie ma.
  const deduped = useMemo(() => {
    const seen = new Map<string, Row>();
    for (const r of rows) {
      const key = r.message_id ?? r.id;
      const existing = seen.get(key);
      if (!existing) {
        seen.set(key, r); // rows już są DESC, więc pierwszy = najnowszy
      } else if (!existing.metadata && r.metadata) {
        // Starszy wpis ma metadata - uzupełnij newest
        seen.set(key, { ...existing, metadata: r.metadata });
      }
    }
    return Array.from(seen.values());
  }, [rows]);

  const templates = useMemo(() => {
    const set = new Set(deduped.map((r) => r.template_name));
    return Array.from(set).sort();
  }, [deduped]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return deduped.filter((r) => {
      if (template !== "all" && r.template_name !== template) return false;
      if (status !== "all" && r.status !== status) return false;
      if (s && !r.recipient_email.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [deduped, template, status, search]);

  const stats = useMemo(() => {
    let sent = 0, failed = 0, suppressed = 0, pending = 0;
    for (const r of filtered) {
      if (r.status === "sent") sent++;
      else if (r.status === "dlq" || r.status === "failed") failed++;
      else if (r.status === "suppressed" || r.status === "bounced" || r.status === "complained") suppressed++;
      else if (r.status === "pending") pending++;
    }
    return { total: filtered.length, sent, failed, suppressed, pending };
  }, [filtered]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <Mail className="w-5 h-5" /> Log wysyłek emaili
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Historia wysyłanych powiadomień (po dedupliacji - najnowszy status na wiadomość). Zakres: {RANGE_LABEL[range]}.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Odśwież
        </Button>
      </div>

      {/* Statystyki */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Łącznie" value={stats.total} icon={<Mail className="w-4 h-4" />} />
        <StatCard label="Wysłane" value={stats.sent} tone="success" icon={<CheckCircle2 className="w-4 h-4" />} />
        <StatCard label="W kolejce" value={stats.pending} tone="info" icon={<Clock className="w-4 h-4" />} />
        <StatCard label="Nieudane" value={stats.failed} tone="error" icon={<AlertCircle className="w-4 h-4" />} />
        <StatCard label="Zablokowane" value={stats.suppressed} tone="warn" icon={<ShieldOff className="w-4 h-4" />} />
      </div>

      {/* Filtry */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Zakres czasu</label>
            <Select value={range} onValueChange={(v) => setRange(v as Range)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Ostatnie 24h</SelectItem>
                <SelectItem value="7d">Ostatnie 7 dni</SelectItem>
                <SelectItem value="30d">Ostatnie 30 dni</SelectItem>
                <SelectItem value="all">Wszystko</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Szablon</label>
            <Select value={template} onValueChange={(v) => { setTemplate(v); setPage(0); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                <SelectItem value="sent">Wysłane</SelectItem>
                <SelectItem value="pending">W kolejce</SelectItem>
                <SelectItem value="dlq">Nieudane</SelectItem>
                <SelectItem value="suppressed">Zablokowane</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Email odbiorcy</label>
            <Input
              placeholder="szukaj po emailu..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : pageRows.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              Brak wyników w wybranym zakresie i filtrach.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Data</th>
                    <th className="text-left px-4 py-3 font-medium">Szablon</th>
                    <th className="text-left px-4 py-3 font-medium">Odbiorca</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium">Szczegóły</th>
                    <th className="text-right px-4 py-3 font-medium">Podgląd</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => {
                    const hasPreview = Boolean(r.metadata?.html || r.metadata?.text);
                    return (
                      <tr key={r.id} className="border-t hover:bg-muted/30">
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {format(new Date(r.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{r.template_name}</td>
                        <td className="px-4 py-3">{r.recipient_email}</td>
                        <td className="px-4 py-3">
                          <Badge className={statusVariant(r.status)} variant="outline">
                            {STATUS_LABEL[r.status] ?? r.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground max-w-md truncate" title={r.error_message ?? r.message_id ?? ""}>
                          {r.error_message ? (
                            <span className="text-red-600 dark:text-red-400">{r.error_message}</span>
                          ) : (
                            <span className="font-mono">{r.message_id ?? "-"}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!hasPreview}
                            onClick={() => setPreviewRow(r)}
                            title={hasPreview ? "Pokaż podgląd maila" : "Brak zapisanego podglądu (starszy wpis)"}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginacja */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Strona {page + 1} z {totalPages} ({filtered.length} wyników)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
              Następna
            </Button>
          </div>
        </div>
      )}

      {/* Podgląd maila */}
      <Dialog open={!!previewRow} onOpenChange={(open) => !open && setPreviewRow(null)}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="text-base">
              {previewRow?.metadata?.subject || "Podgląd maila"}
            </DialogTitle>
            <DialogDescription className="text-xs space-y-0.5">
              <span className="block">
                <strong>Do:</strong> {previewRow?.recipient_email}
              </span>
              <span className="block">
                <strong>Szablon:</strong> <span className="font-mono">{previewRow?.template_name}</span>
              </span>
              {previewRow && (
                <span className="block">
                  <strong>Wysłano:</strong>{" "}
                  {format(new Date(previewRow.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="html" className="flex-1 flex flex-col min-h-0">
            <TabsList className="mx-6 mt-3 self-start">
              <TabsTrigger value="html" disabled={!previewRow?.metadata?.html}>HTML</TabsTrigger>
              <TabsTrigger value="text" disabled={!previewRow?.metadata?.text}>Tekst</TabsTrigger>
            </TabsList>
            <TabsContent value="html" className="flex-1 min-h-0 mt-3 mx-6 mb-6">
              {previewRow?.metadata?.html ? (
                <iframe
                  title="Podgląd HTML"
                  srcDoc={previewRow.metadata.html}
                  className="w-full h-[60vh] border rounded-md bg-white"
                  sandbox=""
                />
              ) : (
                <p className="text-sm text-muted-foreground p-4">Brak wersji HTML.</p>
              )}
            </TabsContent>
            <TabsContent value="text" className="flex-1 min-h-0 mt-3 mx-6 mb-6">
              {previewRow?.metadata?.text ? (
                <pre className="w-full h-[60vh] overflow-auto border rounded-md p-4 text-xs whitespace-pre-wrap bg-muted/30">
                  {previewRow.metadata.text}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground p-4">Brak wersji tekstowej.</p>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({
  label, value, tone, icon,
}: {
  label: string;
  value: number;
  tone?: "success" | "error" | "warn" | "info";
  icon: React.ReactNode;
}) => {
  const toneClass =
    tone === "success" ? "text-emerald-600 dark:text-emerald-400" :
    tone === "error" ? "text-red-600 dark:text-red-400" :
    tone === "warn" ? "text-amber-600 dark:text-amber-400" :
    tone === "info" ? "text-blue-600 dark:text-blue-400" :
    "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          {icon}<span>{label}</span>
        </div>
        <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
};
